<script setup lang="ts">
// components
import NavItem from '~/components/redesign/NavItem.vue';

// composable
import { useI18n } from '#i18n';
import onRouteChange from '~/composables/onRouteChange';
import useSwitchLocaleWithSlash from '~/composables/useSwitchLocaleWithSlash';

// directives
import { vOnClickOutside } from '@vueuse/components';

withDefaults(defineProps<{ reversed?: boolean; optionsCount?: number }>(), {
  reversed: true,
  optionsCount: 5,
});

const { locale, locales } = useI18n();
const switchLocalePathWithSlash = useSwitchLocaleWithSlash();

const opened = ref(false);
const setOpened = (value: boolean) => {
  opened.value = value;
};
const clickOutsideHandler = () => {
  if (opened.value) {
    setOpened(false);
  }
};

onRouteChange(() => setOpened(false));
</script>

<template>
  <div
    v-on-click-outside="clickOutsideHandler"
    data-component-name="LanguageSwitcherFooter"
  >
    <button
      :class="['button', { opened, reversed }]"
      @click="setOpened(!opened)"
    >
      <SvgIcon name="planet" fill="white" />

      {{ locale }}

      <SvgIcon name="chevron-down" fill="white" />
    </button>

    <ul v-if="opened">
      <li v-for="{ code, i18nKey } of locales" :key="code">
        <NavItem
          :to="switchLocalePathWithSlash(code)"
          :selected="locale === code"
          size="small"
        >
          {{ $t(i18nKey) }}
        </NavItem>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
@use '_/fn';
@use '_/mixins/typo';
@use '_/mixins/flex';
@use '_/mixins/size';
@use '_/mixins/media';
@use '_/mixins/component';

[data-component-name='LanguageSwitcherFooter'] {
  $item-height: 2.125rem;

  position: relative;
  width: fit-content;
  user-select: none;

  button {
    @include flex.center-sb;
    @include typo.subtitle-4;

    width: 4.1875rem;
    padding: 0.3125rem 0.125rem;
    text-transform: uppercase;
    background-color: transparent;
    transition: background-color 0.15s ease;
    cursor: pointer;

    svg {
      @include size.fixed(1.5rem);

      &:first-of-type {
        margin-right: 0.25rem;
      }
    }

    &.reversed {
      color: fn.token('text-reversed');
    }

    &::after {
      content: '';
      display: block;
      position: absolute;
      left: 0;
      top: 0;
      right: 0;
      width: 100%;
      height: 4px;
      background-color: transparent;
      transition: background-color 0.15s ease;
    }

    &.opened {
      &::after {
        background-color: fn.token('outline-action');
      }

      svg {
        transform: rotate(-180deg);
      }
    }

    &:hover {
      background-color: rgba(fn.token('accent-hover'), 16%);
    }

    &:active {
      background-color: rgba(fn.token('accent-hover'), 32%);
    }
  }

  ul {
    background-color: fn.token('surf-cont-primary');
    position: absolute;
    z-index: 6;
    bottom: $item-height;
    max-width: 17.5rem;
    width: 17.5rem;
    height: calc($item-height * v-bind(optionsCount));
    overflow-y: auto;
    overscroll-behavior-y: contain;

    transition: all 0.3s ease;

    @include component.scrollbar;

    &::-webkit-scrollbar-thumb {
      background: fn.token('icon-action');
    }
  }
}
</style>
